import { useEffect } from 'preact/hooks';
import { LocationProvider, Router, Route } from 'preact-iso';
import { AuthGuard } from './components/AuthGuard';
import { Toaster } from './components/Toaster';
import { Confirmer } from './components/Confirmer';
import { sessionSignal } from './auth/session';
import { bootstrapAlarms } from './realtime/alarmsStore';
import { bootstrapCommands } from './realtime/commandsStore';
import { bootstrapNotifications } from './realtime/notificationsStore';
import { AlarmHistoryPage } from './pages/AlarmHistoryPage';
import { AlarmsPage } from './pages/AlarmsPage';
import { ArtifactsPage } from './pages/ArtifactsPage';
import { ClientCatalogPage } from './pages/ClientCatalogPage';
import { CommandHistoryPage } from './pages/CommandHistoryPage';
import { DashboardPage } from './pages/DashboardPage';
import { DiscoveryPage } from './pages/DiscoveryPage';
import { ScanCandidatesPage } from './pages/ScanCandidatesPage';
import { IdentityPage } from './pages/IdentityPage';
import { LicensePage } from './pages/LicensePage';
import { DeviceRegistrationsPage } from './pages/DeviceRegistrationsPage';
import { HmiEditorPage } from './pages/HmiEditorPage';
import { HmiPlantPage } from './pages/HmiPlantPage';
import { HmiPreviewPage } from './pages/HmiPreviewPage';
import { WebHmiPage } from './pages/WebHmiPage';
import { PlantUsersPage } from './pages/PlantUsersPage';
import { PortalAdminPage } from './pages/PortalAdminPage';
import { ServicePinPage } from './pages/ServicePinPage';
import { MapPage } from './pages/MapPage';
import { PlantChartsPage } from './pages/PlantChartsPage';
import { PlantsPage } from './pages/PlantsPage';
import { PlantSchemaPage } from './pages/PlantSchemaPage';
import { PlantShellPage } from './pages/PlantShellPage';
import { TouchpanelsPage } from './pages/TouchpanelsPage';
import { TouchpanelDetailPage } from './pages/TouchpanelDetailPage';
import { PlantUpdatesPage } from './pages/PlantUpdatesPage';
import { TypicalsPage } from './pages/TypicalsPage';
import { RecognitionEditorPage } from './pages/RecognitionEditorPage';
import { NotFoundPage } from './pages/NotFoundPage';

export function App() {
  // Kick off the alarms + commands bootstraps once the session becomes known.
  // Both stores live per-user and need the hub cookie to auto-join the right
  // groups; running before /api/me has resolved would 401 and wedge us into
  // the login redirect twice.
  const session = sessionSignal.value;
  useEffect(() => {
    if (session.ready && session.user) {
      void bootstrapAlarms();
      // Commands is a synchronous wire-listener + hub-start; the function is
      // idempotent so this useEffect can fire more than once safely.
      bootstrapCommands();
      // Notifications inbox — same idempotent bootstrap as alarms; seeds the
      // top-bar bell badge before the first push arrives.
      void bootstrapNotifications();
    }
  }, [session.ready, session.user]);

  return (
    <LocationProvider>
      <AuthGuard>
        <Router>
          <Route path="/" component={MapPage} />
          <Route path="/dashboard" component={DashboardPage} />
          <Route path="/dashboard/:plantId" component={DashboardPage} />
          {/* Global alarm/command surfaces are admin-flavoured (all plants
              at once); the per-plant work happens under /plants/:id/*. Both
              variants share the same page components — the plant-scoped
              routes just hand a route :id in that the page reads to lock
              the filter + render PlantSubNav. */}
          <Route path="/alarms" component={AlarmsPage} />
          <Route path="/alarms/history" component={AlarmHistoryPage} />
          <Route path="/commands/history" component={CommandHistoryPage} />
          <Route path="/plants/:id/alarms" component={AlarmsPage} />
          <Route path="/plants/:id/alarms/history" component={AlarmHistoryPage} />
          <Route path="/plants/:id/commands" component={CommandHistoryPage} />
          {/* Discovery is a per-plant workflow; global routes kept as
              redirects to the plant-scoped surface via the pages themselves
              (fall back to plant-picker if no plantId in route). */}
          <Route path="/plants/:id/discovery" component={DiscoveryPage} />
          <Route path="/plants/:id/discovery/candidates" component={ScanCandidatesPage} />
          <Route path="/plants" component={PlantsPage} />
          <Route path="/touchpanels" component={TouchpanelsPage} />
          <Route path="/touchpanels/:id" component={TouchpanelDetailPage} />
          <Route path="/plants/:id" component={PlantSchemaPage} />
          <Route path="/plants/:id/schema" component={PlantSchemaPage} />
          <Route path="/plants/:id/updates" component={PlantUpdatesPage} />
          <Route path="/plants/:id/users" component={PlantUsersPage} />
          <Route path="/plants/:id/shell" component={PlantShellPage} />
          <Route path="/plants/:id/charts" component={PlantChartsPage} />
          {/* Produktive HMI-Route: rendert ein gespeichertes HmiProject
              gegen die Live-Werte DIESER Anlage. Der Gegenpol zu
              /hmi/preview, das den Widget-Katalog gegen einen Simulator
              zeigt. Beide bleiben bestehen: der Katalog ist der
              Sonderfall, diese Route der Normalfall. */}
          <Route path="/plants/:id/hmi" component={HmiPlantPage} />
          {/* Der Editor (J.8). Eigene Route und kein Modus auf der Seite
              darueber: dort laufen die Widgets gegen die echte Anlage und ein
              Klick ist ein Schaltbefehl, hier ist er eine Auswahl. Beides in
              einer Seite zu mischen ist der kuerzeste Weg zu einer Pumpe, die
              jemand beim Anwaehlen startet. Muss VOR der Route darueber
              stehen? Nein: preact-iso waehlt die genauere Route, und
              "/plants/:id/hmi" passt nicht auf "/plants/:id/hmi/editor". */}
          <Route path="/plants/:id/hmi/editor" component={HmiEditorPage} />
          <Route path="/settings/identity" component={IdentityPage} />
          <Route path="/settings/typicals" component={TypicalsPage} />
          <Route path="/settings/recognition" component={RecognitionEditorPage} />
          <Route path="/settings/artifacts" component={ArtifactsPage} />
          <Route path="/settings/client-catalog" component={ClientCatalogPage} />
          <Route path="/settings/licenses" component={LicensePage} />
          <Route path="/settings/device-registrations" component={DeviceRegistrationsPage} />
          <Route path="/settings/portal" component={PortalAdminPage} />
          <Route path="/settings/servicezugang" component={ServicePinPage} />
          <Route path="/hmi/preview" component={HmiPreviewPage} />
          {/* Web-HMI fuer Endkunden: ohne App-Rahmen, nur zugewiesene Anlagen. */}
          <Route path="/m" component={WebHmiPage} />
          <Route path="/m/:plantId" component={WebHmiPage} />
          <Route default component={NotFoundPage} />
        </Router>
        {/* Single Toaster instance for the whole app. Sits above the router so
            it survives navigations. */}
        <Toaster />
        {/* Global Confirm-Dialog — ersetzt window.confirm() überall im App-
            Chrome durch einen styleable Preact-Modal. Nur ein Dialog gleich-
            zeitig; siehe components/Confirmer.tsx. */}
        <Confirmer />
      </AuthGuard>
    </LocationProvider>
  );
}
